import { useEffect, useState } from "react";
import { X, Phone, Copy, Check, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetClose } from "@/components/ui/sheet";
import { toast } from "@/components/ui/use-toast";
import { Patient } from "./PatientCard";
import { ActivityTimeline } from "./ActivityTimeline";
import { useActivities } from "@/hooks/useActivities";
import { usePatientTATTimer } from "@/hooks/usePatientTATTimer";
import { useTotalElapsedTime } from "@/hooks/useTotalElapsedTime";
import { useMDCompletionTime } from "@/hooks/useMDCompletionTime";
import { cn } from "@/lib/utils";

interface PatientDetailDrawerProps {
  patient: Patient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getTATColor = (status?: string) => {
  switch (status) {
    case 'green': return 'text-success';
    case 'yellow': return 'text-warning';
    case 'red': return 'text-destructive';
    case 'exceeded': return 'text-destructive font-bold';
    default: return 'text-muted-foreground';
  }
};

const parseDateSafe = (value?: string | Date | null) => {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  if (isNaN(date.getTime())) return null;
  return date;
};

const getLatestCompletion = (steps: any[], workflowStep: string) => {
  let latest: Date | null = null;
  steps.forEach((step) => {
    if (step?.workflowStep !== workflowStep) return;
    const completedAt = parseDateSafe(step?.completionTime);
    if (!completedAt) return;
    if (!latest || completedAt.getTime() > latest.getTime()) {
      latest = completedAt;
    }
  });
  return latest;
};

export const PatientDetailDrawer = ({ patient, open, onOpenChange }: PatientDetailDrawerProps) => {
  const [copied, setCopied] = useState(false);
  
  // Debug: Extensive logging to trace UUID issue
  console.log('🔍 PatientDetailDrawer CALLED');
  console.log('🔍 patient object:', JSON.stringify(patient, null, 2));
  console.log('🔍 patient?.uuid:', patient?.uuid);
  console.log('🔍 patient?.id:', patient?.id);
  console.log('🔍 typeof patient?.uuid:', typeof patient?.uuid);
  console.log('🔍 typeof patient?.id:', typeof patient?.id);
  
  const patientUuid = patient?.uuid;
  console.log('🔍 patientUuid to be passed to useActivities:', patientUuid);
  
  const { data: activities = [], isLoading, refetch: refetchActivities } = useActivities(patientUuid || null);

  useEffect(() => {
    if (open && patientUuid) {
      refetchActivities();
    }
  }, [open, patientUuid, refetchActivities]);
  
  const getCopyValue = () => {
    const admission = patient.admissionNumber?.trim();
    const fallbackId = patient.id?.trim();
    return admission || fallbackId || "";
  };

  const handleCopyAdmissionNumber = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const textToCopy = getCopyValue();
    if (!textToCopy) {
      toast({ title: "Nothing to copy", description: "Admission number is missing." });
      return;
    }
    const copyWithFallback = () => {
      const input = document.createElement("textarea");
      input.value = textToCopy;
      input.setAttribute("readonly", "true");
      input.style.position = "fixed";
      input.style.opacity = "0";
      input.style.pointerEvents = "none";
      document.body.appendChild(input);
      input.focus();
      input.select();
      input.setSelectionRange(0, textToCopy.length);
      const copiedOk = document.execCommand("copy");
      document.body.removeChild(input);
      return copiedOk;
    };
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        toast({ title: "Copied", description: textToCopy });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
      const copiedOk = copyWithFallback();
      if (copiedOk) {
        toast({ title: "Copied", description: textToCopy });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
      throw new Error("execCommand failed");
    } catch {
      const manualCopy = window.prompt("Copy admission number:", textToCopy);
      if (manualCopy !== null) {
        toast({ title: "Copy manually", description: "Press Ctrl+C to copy." });
      } else {
        toast({ title: "Copy failed", description: "Clipboard access blocked." });
      }
    }
  };
  const tatTimer = usePatientTATTimer(patient?.uuid || patient.id);
  const { data: mdCompletionTime } = useMDCompletionTime(patient?.uuid || patient.id);
  const totalElapsedTime = useTotalElapsedTime(mdCompletionTime, {
    dischargeStatus: patient.dischargeStatus,
    dischargeCompletionTime: (patient as any).physicalDischargeCompletionTime,
  });

  const expectedDischargeRaw = patient.expectedDischarge || patient.dischargeDate;
  const expectedDischargeDate = parseDateSafe(expectedDischargeRaw);
  const pdCompletionDate = getLatestCompletion(activities, "PD");
  const dischargeCompletedDate =
    parseDateSafe((patient as any).physicalDischargeCompletionTime) || pdCompletionDate;
  const completedOrTodayDate = dischargeCompletedDate || new Date();
  const daysExceeded = expectedDischargeDate
    ? Math.floor((completedOrTodayDate.getTime() - expectedDischargeDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const showExceeded = !!expectedDischargeDate && daysExceeded > 0;
  
  // Debug: Log fetched activities
  console.log('🔍 PatientDetailDrawer - Total activities:', activities.length);
  console.log('🔍 PatientDetailDrawer - Activities:', activities);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[800px] sm:max-w-[800px] p-0">
        {/* Custom Header */}
        <div className="pt-3 px-3.5 pb-6 border-b flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className="text-xs text-muted-foreground flex-shrink-0">{patient.id}</span>
            <span className="text-foreground font-semibold text-sm cursor-pointer hover:underline truncate min-w-0">{patient.name}</span>
            <div className="flex-shrink-0">
              {patient.dischargeStatus === "discharged" ? (
                <Badge className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/10 whitespace-nowrap">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mr-1.5" />
                  Discharge Complete
                </Badge>
              ) : mdCompletionTime ? (
                <Badge className="bg-success/10 text-success border border-success/20 hover:bg-success/10 whitespace-nowrap">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mr-1.5" />
                  Discharge Initiated
                </Badge>
              ) : (
                <Badge variant="secondary" className="whitespace-nowrap">
                  <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mr-1.5" />
                  Discharge Not Initiated
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {patient.dischargeStatus === "discharged" ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Current:</span>
                <span className="bg-muted/10 text-muted-foreground px-2 py-0.5 rounded-md font-mono text-base font-bold">
                  --:--:--
                </span>
              </div>
            ) : tatTimer && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Current:</span>
                <span className={cn(
                  "px-2 py-0.5 rounded-md font-mono text-base font-bold",
                  tatTimer.status === 'exceeded' ? "bg-destructive/10 text-destructive" :
                  tatTimer.status === 'red' ? "bg-destructive/10 text-destructive" :
                  tatTimer.status === 'yellow' ? "bg-warning/10 text-warning" :
                  "bg-success/10 text-success"
                )}>
                  {tatTimer.elapsedTime}
                </span>
              </div>
            )}
            {totalElapsedTime && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Total:</span>
                <span className="bg-muted/10 text-muted-foreground px-2 py-0.5 rounded-md font-mono text-base font-bold">
                  {totalElapsedTime}
                </span>
              </div>
            )}
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </div>

        {/* Scrollable Content */}
        <ScrollArea className="h-[calc(100vh-65px)]">
          {/* Patient Details Grid */}
          <div className="grid grid-cols-4 border border-border">
            {/* Row 1 */}
            <div className="border-r border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Name</div>
              <div className="flex items-center gap-1">
                <User className="h-4 w-4 text-muted-foreground" />
                <Button
                  variant="link"
                  className="text-primary p-0 h-auto text-sm font-normal leading-[20px] tracking-[-0.084px] text-left break-words whitespace-normal"
                >
                  {patient.name}
                </Button>
              </div>
            </div>
            <div className="border-r border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Gender / Age</div>
              <div className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                {patient.gender}, {patient.age}
              </div>
            </div>
            <div className="border-r border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Ward / Bed</div>
              <div className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                {patient.ward}, {patient.bed}
              </div>
            </div>
            <div className="border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Phone</div>
              <div className="flex items-center gap-1 text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                <Phone className="h-4 w-4 text-muted-foreground" />
                {patient.phone}
              </div>
            </div>

            {/* Row 2 */}
            <div className="border-r border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Admission Number</div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px] truncate">
                  {patient.admissionNumber}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0"
                  onClick={handleCopyAdmissionNumber}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-success" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
            <div className="border-r border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Admission Date & Time</div>
              <div className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                {patient.admissionDate}
              </div>
            </div>
            <div className="border-r border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Expected Discharge Date</div>
              <div className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                {patient.expectedDischarge || patient.dischargeDate || '-'}
              </div>
            </div>
            <div className="border-b border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Consultant Doctor</div>
              <div className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                {patient.doctor}
              </div>
            </div>

            {/* Row 3 */}
            <div className="border-r border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Diagnosis</div>
              <div className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                {patient.diagnosis}
              </div>
            </div>
            <div className="border-r border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">LOS</div>
              <div className="text-sm font-normal text-foreground leading-[20px] tracking-[-0.084px]">
                {patient.los}
              </div>
              {showExceeded && (
                <div className="mt-1 text-xs font-medium text-destructive">
                  Exceeded: {daysExceeded} Days
                </div>
              )}
            </div>
            <div className="border-r border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Current Activity Time</div>
              {patient.dischargeStatus === "discharged" ? (
                <div className="flex flex-col gap-0.5">
                  <div className="text-sm font-semibold text-muted-foreground">--:--:--</div>
                  <div className="text-xs text-muted-foreground">Completed</div>
                </div>
              ) : tatTimer ? (
                <div className="flex flex-col gap-0.5">
                  <div className={cn("text-sm font-semibold", getTATColor(tatTimer.status))}>
                    {tatTimer.elapsedTime}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {tatTimer.activityName}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">-</div>
              )}
            </div>
            <div className="p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1 leading-normal">Total Elapsed Time</div>
              <div className="text-sm font-semibold text-muted-foreground">
                {totalElapsedTime || "-"}
              </div>
            </div>
          </div>

          {/* Activity Section */}
          <div className="px-3 py-3 border-t">
            <h3 className="text-base font-semibold mb-4">Activity</h3>
            {isLoading ? (
              <div className="text-center py-2 text-muted-foreground text-sm">Loading activities...</div>
            ) : activities.length === 0 ? (
              <div className="text-center py-2 text-muted-foreground text-sm">No activities recorded</div>
            ) : (
              <ActivityTimeline steps={activities} />
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
